import React from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Avatar, 
  Menu, 
  MenuItem, 
  IconButton,
  Divider 
} from '@mui/material';
import { AccountCircle, ExitToApp } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

interface UserProfileProps {
  collapsed?: boolean;
}

export const UserProfile: React.FC<UserProfileProps> = ({ collapsed = false }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
  };

  // Function to get user initials from display name
  const getUserInitials = (): string => {
    if (!user?.userDisplayName) return 'U';
    
    const nameParts = user.userDisplayName.trim().split(' ');
    if (nameParts.length >= 2) {
      // Get first and last name initials
      return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
    } else if (nameParts.length === 1) {
      // Single name, use first two characters
      return nameParts[0].substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  if (!isAuthenticated || !user) {
    return null;
  }

  // Avatar positioning: Keep avatar in same visual position during sidebar transitions
  // Position it to appear centered when collapsed but stay put when expanded
  const avatarLeftPosition = 0; // Start at left edge of content area

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '48px' }}>
      {/* Avatar - same position in both states */}
      <IconButton
        size="large"
        aria-label="account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={handleMenu}
        color="inherit"
        sx={{ 
          position: 'absolute',
          left: '-10px',
          top: '0px',
          padding: 0 
        }}
      >
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
          {getUserInitials()}
        </Avatar>
      </IconButton>

      {/* User info - only show when expanded */}
      {!collapsed && (
        <Box sx={{ 
          marginLeft: '44px', // avatar width + gap
          paddingTop: '8px'
        }}>
          <Typography 
            variant="subtitle2" 
            fontWeight="600"
            sx={{ 
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '14px',
              lineHeight: '18px',
              margin: 0
            }}
          >
            {user.userDisplayName}
          </Typography>
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ 
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '12px',
              lineHeight: '16px',
              display: 'block',
              margin: 0
            }}
          >
            {user.audienceName}
          </Typography>
        </Box>
      )}
      
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {user.userDisplayName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user.userName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user.audienceName}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ExitToApp sx={{ mr: 1 }} />
          Sign Out
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UserProfile; 